import type { ProductInterface, StockStatusElements } from "@jti/fe-common";
import type { IBundleItem, IBundleItemOption, IProductStockStatus } from "@jti/magento";
import type {
  ApiMagentoProduct,
  ApiMagentoProductItem,
  ApiMagentoProductItemOption,
  ApiMagentoProductTypeName,
} from "@jti/magento-types";

import getUrlByRole from "./getUrlByRole";

const getStockStatusesForOptions = (options: ApiMagentoProductItemOption[], isConfigurableProduct: boolean) => {
  return options.map((option) => ({
    key: isConfigurableProduct ? option?.product?.sku : option.id,
    value: option?.product?.stock_status,
  }));
};

const getGalleryOptions = (productData: any, callback: (array: IBundleItemOption[]) => any /* TODO: fix me */) =>
  productData?.items
    ?.map((item: IBundleItem) => callback(item?.all_options))
    .reduce(
      (prev: IBundleItemOption, option: IBundleItemOption) => ({
        ...prev,
        ...option,
      }),
      {}
    );

export const getStockStatusesForBundleItems = (productData: ApiMagentoProduct): StockStatusElements => {
  const productType: ApiMagentoProductTypeName = productData?.__typename;
  const isConfigurableProduct = productType === "ConfigurableProduct";

  const getStockStatuses = (items: ApiMagentoProductItem[]): Record<string, IProductStockStatus> => {
    return items?.reduce((accumulatedStatuses, item) => {
      const stockStatuses = getStockStatusesForOptions(item?.all_options ?? [], isConfigurableProduct);

      const stockStatusesObject = stockStatuses?.reduce(
        (statusMap, { key, value }) => ({
          ...statusMap,
          [key]: value,
        }),
        {}
      );

      return {
        ...accumulatedStatuses,
        ...stockStatusesObject,
      };
    }, {});
  };

  return getStockStatuses(productData.items ?? []);
};

export default function getOptions(
  productData: ProductInterface,
  getType: "gallery" | "stock"
): Record<string, IProductStockStatus> | Record<string, string> | StockStatusElements {
  const productType: ApiMagentoProductTypeName = productData?.__typename;
  const isConfigurableProduct = productType === "ConfigurableProduct";

  const callback = (array: IBundleItemOption[]) =>
    array?.reduce(
      (prev, option) => ({
        ...prev,
        [isConfigurableProduct ? option?.product?.sku : option.id]:
          getType === "gallery" ? getUrlByRole(option?.product?.media_gallery) : option?.product?.stock_status,
      }),
      {}
    );

  if (isConfigurableProduct) {
    return getType === "gallery" ? {} : callback(productData?.variants);
  }

  return getType === "gallery" ? getGalleryOptions(productData, callback) : getStockStatusesForBundleItems(productData);
}
